import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import { ExtractedResourceProvenance } from './ExtractedResourceProvenance';

const procedureFhirService = new FhirService('Procedure');
const dateTimeParser = new FhirDateTime();

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
  codeCell: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  modalSection: {
    marginBottom: 24,
  },
  modalSectionTitle: {
    marginBottom: 12,
    fontSize: '1rem',
    fontWeight: 600,
  },
  modalField: {
    marginBottom: 12,
  },
  modalLabel: {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600,
  },
  modalValue: {
    display: 'block',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: 1.5,
  },
}));

const getProcedureCodeDisplay = (resource: fhir.Procedure): string =>
  resource.code?.coding?.[0]?.display || resource.code?.text || '—';

const getPerformedDisplay = (resource: fhir.Procedure): string => {
  if (resource.performedDateTime) {
    return dateTimeParser.getDisplayValue(resource.performedDateTime) || '—';
  }
  if (resource.performedPeriod?.start) {
    return (
      dateTimeParser.getDisplayValue(resource.performedPeriod.start) || '—'
    );
  }
  return '—';
};

const getPerformerDisplay = (resource: fhir.Procedure): string => {
  const performer = resource.performer?.[0];
  if (!performer) return '—';
  const actor = performer.actor;
  if (!actor) return '—';
  if (typeof actor === 'object' && 'display' in actor && actor.display) {
    return actor.display;
  }
  return '—';
};

type ProceduresRow = {
  id: string;
  resource: fhir.Procedure;
  code: string;
  status: string;
  performed: string;
  performer: string;
  category: string;
};

type ProcedureViewModalProps = {
  resource: fhir.Procedure;
  subjectId: string;
  onDismiss: () => void;
  onRejected: (resourceId: string) => void;
};

const ProcedureViewModal: React.FC<ProcedureViewModalProps> = ({
  resource,
  subjectId,
  onDismiss,
  onRejected,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const codeDisplay = getProcedureCodeDisplay(resource);
  const status = resource.status || '—';
  const performed = getPerformedDisplay(resource);
  const performer = getPerformerDisplay(resource);
  const category =
    resource.category?.coding?.[0]?.display || resource.category?.text || '—';
  const reason =
    resource.reasonCode?.[0]?.coding?.[0]?.display ||
    resource.reasonCode?.[0]?.text ||
    null;

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={codeDisplay}
      data-testid="procedure-view-modal"
    >
      <Box padding={2} direction="column">
        <div className={classes.modalSection}>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.proceduresCode)}
            </span>
            <span className={classes.modalValue}>{codeDisplay}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.proceduresStatus)}
            </span>
            <span className={classes.modalValue}>{status}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.proceduresPerformed)}
            </span>
            <span className={classes.modalValue}>{performed}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.proceduresPerformer)}
            </span>
            <span className={classes.modalValue}>{performer}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.proceduresCategory)}
            </span>
            <span className={classes.modalValue}>{category}</span>
          </div>
          {reason && (
            <div className={classes.modalField}>
              <span className={classes.modalLabel}>
                {formatMessage(memberDetailMessages.proceduresReason)}
              </span>
              <span className={classes.modalValue}>{reason}</span>
            </div>
          )}
        </div>
        <ExtractedResourceProvenance
          resource={resource}
          resourceType="Procedure"
          subjectId={subjectId}
          onRejected={onRejected}
        />
      </Box>
    </Modal>
  );
};

export type ProceduresContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const ProceduresContent: React.FC<ProceduresContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const dataset = useDataset();
  const [fetchProcedures, fetchState] = useService(procedureFhirService.get);
  const [procedures, setProcedures] = useState<fhir.Procedure[]>([]);
  const [selectedProcedure, setSelectedProcedure] =
    useState<fhir.Procedure | null>(null);

  const loadProcedures = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const response = await fetchProcedures(accountId, {
        subject: `Patient/${memberId}`,
        _tag: [dataset.codingToken],
        _count: 200,
      });
      const entry = response?.data?.entry ?? [];
      const resources = entry
        .map((e: { resource?: fhir.Procedure }) => e.resource)
        .filter(Boolean) as fhir.Procedure[];
      const sortedResources = resources.sort((a, b) => {
        const dateA = getResourcePerformedTime(a);
        const dateB = getResourcePerformedTime(b);
        return dateB - dateA;
      });
      setProcedures(sortedResources);
    } catch {
      setProcedures([]);
    }
  }, [accountId, memberId, auth, fetchProcedures, dataset.codingToken]);

  useEffect(() => {
    void loadProcedures();
  }, [loadProcedures]);

  const rows: ProceduresRow[] = procedures.map(resource => ({
    id: resource.id ?? '',
    resource,
    code: getProcedureCodeDisplay(resource),
    status: resource.status || '—',
    performed: getPerformedDisplay(resource),
    performer: getPerformerDisplay(resource),
    category:
      resource.category?.coding?.[0]?.display || resource.category?.text || '—',
  }));

  const columns = [
    {
      header: {
        label: formatMessage(memberDetailMessages.proceduresCode),
      },
      cell: {
        content: (row: ProceduresRow) => row.code,
        className: classes.codeCell,
      },
    },
    {
      header: { label: formatMessage(memberDetailMessages.proceduresStatus) },
      cell: { content: (row: ProceduresRow) => row.status },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.proceduresPerformed),
      },
      cell: { content: (row: ProceduresRow) => row.performed },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.proceduresPerformer),
      },
      cell: { content: (row: ProceduresRow) => row.performer },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.proceduresCategory),
      },
      cell: { content: (row: ProceduresRow) => row.category },
    },
  ];

  const handleRowClick = (row: ProceduresRow) => {
    setSelectedProcedure(row.resource);
  };

  if (fetchState.isFetching && procedures.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <TableModule
        isLoading={fetchState.isFetching}
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(memberDetailMessages.proceduresEmpty)}
        onRowClick={handleRowClick}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        rowRole="link"
      />
      {selectedProcedure && (
        <ProcedureViewModal
          resource={selectedProcedure}
          subjectId={memberId}
          onDismiss={() => setSelectedProcedure(null)}
          onRejected={resourceId => {
            setProcedures(current =>
              current.filter(resource => resource.id !== resourceId),
            );
            setSelectedProcedure(null);
          }}
        />
      )}
    </div>
  );
};

function getResourcePerformedTime(resource: fhir.Procedure): number {
  if (resource.performedDateTime) {
    return new Date(resource.performedDateTime).getTime();
  }
  if (resource.performedPeriod?.start) {
    return new Date(resource.performedPeriod.start).getTime();
  }
  return 0;
}
